import { getDiasUteisMes } from '@/utils/diasUteis';
import { convertHoursToDecimal } from './timeConversion';
import { ProcessingLog, OSData, UpdateData } from '@/types/reprocessamento';

export interface PrecomputedAggregates {
  /** Map<"opNorm|YYYY-MM", Set<data_real>> — distinct days worked per operator per month */
  operadorMesDias: Map<string, Set<string>>;
  /** Map<"opNorm|data_real", number> — count of non-cancelled OS per operator per day */
  operadorDiaOsCount: Map<string, number>;
}

/**
 * Pre-compute all aggregates from the OS list so that calculateCustos
 * never needs to hit the database.
 */
export function precomputeAggregates(ordens: OSData[]): PrecomputedAggregates {
  const operadorMesDias = new Map<string, Set<string>>();
  const operadorDiaOsCount = new Map<string, number>();

  for (const os of ordens) {
    if (!os.operador_nome || !os.data_real) continue;
    const opNorm = os.operador_nome.trim().toLowerCase();
    const dataStr = os.data_real.slice(0, 10);
    const statusLower = (os.status_descricao || '').toLowerCase();
    const isCancelled = statusLower === 'cancelada';

    const partesData = dataStr.split('-');
    const mesYM = `${partesData[0]}-${partesData[1]}`;
    const mesKey = `${opNorm}|${mesYM}`;

    if (!operadorMesDias.has(mesKey)) operadorMesDias.set(mesKey, new Set());
    operadorMesDias.get(mesKey)!.add(dataStr);

    if (!isCancelled) {
      const diaKey = `${opNorm}|${dataStr}`;
      operadorDiaOsCount.set(diaKey, (operadorDiaOsCount.get(diaKey) || 0) + 1);
    }
  }

  return { operadorMesDias, operadorDiaOsCount };
}

/**
 * Pure in-memory cost calculation — zero database calls.
 * All aggregates come from precomputeAggregates().
 */
export function calculateCustosBatch(
  ordens: OSData[],
  funcionarioMap: Map<string, any>,
  clienteMap: Map<string, any>,
  aggregates: PrecomputedAggregates,
  impostoPercentual: number
): { updates: UpdateData[]; logs: ProcessingLog[]; errors: number } {
  const updates: UpdateData[] = [];
  const logs: ProcessingLog[] = [];
  let errors = 0;

  for (const os of ordens) {
    try {
      if (!os.operador_nome || !os.data_real) {
        errors++;
        continue;
      }

      const opNorm = os.operador_nome.trim().toLowerCase();
      const funcionario = funcionarioMap.get(opNorm);
      if (!funcionario) {
        errors++;
        continue;
      }

      const empresaNorm = (os.empresa_nome || '').trim().toLowerCase();
      const cliente = clienteMap.get(empresaNorm);

      const dataStr = os.data_real.slice(0, 10);
      const dataOS = new Date(dataStr);

      const valorHoraFuncionarioBase =
        getValorHoraHistorico(funcionario.valores_historico, dataOS) || 10.64;

      let valorHoraCliente = 0;
      if (cliente) {
        valorHoraCliente =
          getValorHoraHistorico(cliente.valores_historico, dataOS) || 0;
      }

      const horasExecutadas = convertHoursToDecimal(os.horas_totais || '0');
      const horasFaturadas = convertHoursToDecimal(os.horas_faturadas || '0');

      const valorFaturamento =
        Math.round(horasFaturadas * valorHoraCliente * 100) / 100;
      const imposto =
        Math.round(valorFaturamento * impostoPercentual * 100) / 100;

      const custoBase = horasExecutadas * valorHoraFuncionarioBase;

      let custoExtra = 0;
      let detalheCustoExtra = '';
      const valorAnterior = os.valor_a_pagar || 0;

      const statusLower = (os.status_descricao || '').toLowerCase();
      const isCancelled = statusLower === 'cancelada';

      if (!isCancelled) {
        const diaKey = `${opNorm}|${dataStr}`;
        const quantidadeOSNoDia = aggregates.operadorDiaOsCount.get(diaKey) || 1;

        const partesData = dataStr.split('-');
        const mesYM = `${partesData[0]}-${partesData[1]}`;
        const mesKey = `${opNorm}|${mesYM}`;
        const diasTrabalhados = aggregates.operadorMesDias.get(mesKey)?.size || 1;

        if (funcionario.regime === 'CLT') {
          const valorDiarioVA = funcionario.vale_alimentacao || 0;
          const valeAlimentacaoTotal = valorDiarioVA * diasTrabalhados;
          const planoSaudeMensal = funcionario.plano_saude || 0;
          const custoTotalBeneficios = valeAlimentacaoTotal + planoSaudeMensal;
          const custoExtraDiario =
            diasTrabalhados > 0 ? custoTotalBeneficios / diasTrabalhados : 0;
          custoExtra = quantidadeOSNoDia > 0 ? custoExtraDiario / quantidadeOSNoDia : 0;
          detalheCustoExtra = `CLT: (VA R$${valeAlimentacaoTotal.toFixed(2)} + PS R$${planoSaudeMensal.toFixed(2)}) / ${diasTrabalhados}d / ${quantidadeOSNoDia}OS = R$${custoExtra.toFixed(2)}`;
        } else if (funcionario.regime === 'PJ') {
          const ajudaCusto = funcionario.ajuda_custo || 0;
          const custoExtraDiario =
            diasTrabalhados > 0 ? ajudaCusto / diasTrabalhados : 0;
          custoExtra = quantidadeOSNoDia > 0 ? custoExtraDiario / quantidadeOSNoDia : 0;
          detalheCustoExtra = `PJ: R$${ajudaCusto.toFixed(2)} / ${diasTrabalhados}d / ${quantidadeOSNoDia}OS = R$${custoExtra.toFixed(2)}`;
        }
      }

      let valorAPagar = isCancelled ? 0 : custoBase + custoExtra;
      valorAPagar = Math.round(valorAPagar * 100) / 100;

      updates.push({
        numero: os.numero,
        valor_faturamento: valorFaturamento,
        imposto,
        valor_a_pagar: valorAPagar,
      });

      logs.push({
        numero: os.numero,
        operador: os.operador_nome,
        data: os.data_real,
        valorAnterior,
        valorNovo: valorAPagar,
        detalhes: `Base: ${horasExecutadas.toFixed(2)}h x R$${valorHoraFuncionarioBase.toFixed(2)} = R$${custoBase.toFixed(2)} + ${detalheCustoExtra}`,
        diasCalculados: aggregates.operadorDiaOsCount.get(`${opNorm}|${dataStr}`) || 0,
      });
    } catch {
      errors++;
    }
  }

  return { updates, logs, errors };
}

export const getValorHoraHistorico = (
  historico: any[] | null | undefined,
  dataOS: Date
): number => {
  if (!historico?.length) return 0;
  const tLim = dataOS.getTime();
  let bestT = -Infinity;
  let bestV = 0;
  for (const h of historico) {
    const t = new Date(h.data_reajuste).getTime();
    if (t <= tLim && t >= bestT) {
      bestT = t;
      bestV = Number(h.valor_hora) || 0;
    }
  }
  return bestV;
};
